import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DrcAvatar } from '@/components/Avatar';
import { BackIcon, MoreIcon, NewChatIcon } from '@/components/Icons';
import { AiStaff } from '@/components/AiLeo';
import { STAFF_ASSISTANT_NAME } from '@/lib/assistant';
import { AskChatMenu } from '@/components/AskMenus';
import { useOverlay } from '@/components/Overlay';
import { useAuth } from '@/lib/auth';
import { useGoBack } from '@/lib/goBack';
import { useChats } from '@/data/store';
import { loadAsk, useAsk } from '@/lib/askStore';
import { DrcConversation, DrcHistory, SAMPLE_ASK, SAMPLE_PROJECTS } from '@/screens/DrcDesk';

/**
 * Ask on a phone (Gary, 18 Oct: history on phones too): the list of your chats with the staff assistant, as on a computer,
 * and a chat opens full screen with a back arrow, its title, a new chat button and its menu (Star, Rename,
 * Move to project, Delete). New chat is ?new=1, a saved one ?c=.
 */
export function DrcScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const goBack = useGoBack();
  // A deleted chat steps back to the Ask list, so the next Back leaves Ask instead of showing the list twice
  // (regression run, 19 Oct). After the closing sheet's own history step has gone.
  const leaveDeleted = () => {
    const go = () => goBack('/drc');
    if ((window.history.state as Record<string, unknown> | null)?.chatsSheet) window.addEventListener('popstate', go, { once: true });
    else go();
  };
  const { session } = useAuth();
  const { live } = useChats();
  const { openSheet } = useOverlay();
  const { chats, projects } = useAsk();
  const active = params.get('c');
  const open = !!active || params.has('new');

  // Opened straight to a chat or a project: the titles and projects still load (mobile review, 18 Oct).
  useEffect(() => {
    if (open && chats === null) void (live && session?.user.id ? loadAsk(session.user.id) : loadAsk(null, { chats: SAMPLE_ASK, projects: SAMPLE_PROJECTS }));
  }, [open, chats, live, session?.user.id]);

  if (!open) return <DrcHistory />;
  const summary = (chats ?? []).find((c) => c.id === active) ?? null;
  const project = projects.find((p) => p.id === (summary?.project_id ?? params.get('project'))) ?? null;
  return (
    <div className="chats-conv">
      <header className="chats-topbar">
        {/* Steps back, as the phone's Back does, so the next Back never reopens this chat (mobile review, 19 Oct). */}
        <button type="button" className="chats-icon-btn" aria-label={`Back to your chats with ${STAFF_ASSISTANT_NAME}`} onClick={() => goBack('/drc')}><BackIcon /></button>
        <DrcAvatar />
        <span className="chats-conv-titles">
          <span className="chats-conv-name">{summary?.title || (active ? 'Chat' : 'New chat')}</span>
          <span className="chats-conv-sub">{project ? project.name : <AiStaff />}</span>
        </span>
        {active && <button type="button" className="chats-icon-btn" aria-label="New chat" onClick={() => navigate(project ? `/drc?new=1&project=${project.id}` : '/drc?new=1')}><NewChatIcon /></button>}
        {summary && (
          <button type="button" className="chats-icon-btn" aria-label="More"
            onClick={() => openSheet(<AskChatMenu chat={summary} onDeleted={leaveDeleted} />)}><MoreIcon /></button>
        )}
      </header>
      <DrcConversation phone />
    </div>
  );
}
